import React, { useState, useMemo } from "react";
import { formatCurrencyShort, formatCurrencyCompact } from "../../utils/formatters";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceDot,
} from "recharts";
import { Link } from "react-router-dom";
import DemoCTA from "./DemoCTA";
import { DEMO_PROJECTIONS_INITIAL, DEMO_SAVINGS_RATE } from "./demoData";

// ============================================================================
// CONSTANTS
// ============================================================================

const ASSET_RETURNS = { stocks: 10, bonds: 5, cash: 2 };
const INFLATION_RATE = 3; // Average annual inflation rate

const SCENARIOS = {
    conservative: { name: "Conservative", volatility: 8, allocation: { stocks: 30, bonds: 60, cash: 10 } },
    moderate: { name: "Moderate", volatility: 12, allocation: { stocks: 60, bonds: 30, cash: 10 } },
    aggressive: { name: "Aggressive", volatility: 18, allocation: { stocks: 90, bonds: 5, cash: 5 } },
};

const MILESTONES = [100000, 250000, 500000, 750000, 1000000, 2000000, 5000000];

// Demo uses fixed settings (defined outside component for stable reference)
const DEMO_SETTINGS = {
    goal: 1000000,
    monthlyContribution: 1000,
    annualIncrease: 2,
    years: 20,
    allocation: { stocks: 60, bonds: 30, cash: 10 }, // SCENARIOS.moderate.allocation
    volatility: 12, // SCENARIOS.moderate.volatility
    showRange: true,
    adjustForInflation: false,
};

// ============================================================================
// CALCULATIONS
// ============================================================================

function calculateExpectedReturn(allocation) {
    return (
        (allocation.stocks / 100) * ASSET_RETURNS.stocks +
        (allocation.bonds / 100) * ASSET_RETURNS.bonds +
        (allocation.cash / 100) * ASSET_RETURNS.cash
    );
}

function runProjection({ startingAmount, monthlyContribution, annualIncrease, expectedReturn, volatility, years, simulations = 300 }) {
    // Convert annual return to monthly using geometric formula: (1 + r)^(1/12) - 1
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const monthlyVol = (volatility / 100) / Math.sqrt(12);
    const totalMonths = years * 12;

    function randomNormal() {
        let u = 0, v = 0;
        while (u === 0) u = Math.random();
        while (v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }

    const allPaths = [];
    for (let sim = 0; sim < simulations; sim++) {
        const path = [];
        let balance = startingAmount;
        let contribution = monthlyContribution;
        let totalContributions = startingAmount;

        for (let month = 0; month <= totalMonths; month++) {
            if (month > 0 && month % 12 === 0) {
                contribution *= (1 + annualIncrease / 100);
            }
            if (month > 0) {
                const r = monthlyReturn + monthlyVol * randomNormal();
                balance = balance * (1 + r) + contribution;
                totalContributions += contribution;
            }
            if (month % 12 === 0) {
                path.push({ year: month / 12, balance, contributions: totalContributions });
            }
        }
        allPaths.push(path);
    }

    const data = [];
    for (let y = 0; y <= years; y++) {
        const values = allPaths.map(p => p[y].balance).sort((a, b) => a - b);
        const contributions = allPaths[0][y].contributions;
        data.push({
            year: y,
            p10: Math.round(values[Math.floor(simulations * 0.1)]),
            p50: Math.round(values[Math.floor(simulations * 0.5)]),
            p90: Math.round(values[Math.floor(simulations * 0.9)]),
            contributions: Math.round(contributions),
            range: [Math.round(values[Math.floor(simulations * 0.1)]), Math.round(values[Math.floor(simulations * 0.9)])],
        });
    }

    return data;
}

function findYearToReachGoal(data, goal) {
    for (let i = 0; i < data.length; i++) {
        if (data[i].p50 >= goal) return { year: i, amount: data[i].p50 };
    }
    return null;
}

function calculateProbability({ startingAmount, targetAmount, monthlyContribution, annualIncrease, years, expectedReturn, volatility, simulations = 500 }) {
    // Convert annual return to monthly using geometric formula: (1 + r)^(1/12) - 1
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const monthlyVol = (volatility / 100) / Math.sqrt(12);
    const totalMonths = years * 12;

    function randomNormal() {
        let u = 0, v = 0;
        while (u === 0) u = Math.random();
        while (v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }

    let success = 0;
    for (let sim = 0; sim < simulations; sim++) {
        let balance = startingAmount;
        let contribution = monthlyContribution;
        for (let month = 0; month < totalMonths; month++) {
            if (month > 0 && month % 12 === 0) contribution *= (1 + annualIncrease / 100);
            const r = monthlyReturn + monthlyVol * randomNormal();
            balance = balance * (1 + r) + contribution;
        }
        if (balance >= targetAmount) success++;
    }
    return (success / simulations) * 100;
}

function getRelevantMilestones(current, projected) {
    return MILESTONES.filter(m => m > current && m <= projected * 1.2);
}

// Deterministic projection for what-if comparisons (no random variance)
function runDeterministicProjection({ startingAmount, monthlyContribution, annualIncrease, expectedReturn, years }) {
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const totalMonths = years * 12;

    let balance = startingAmount;
    let contribution = monthlyContribution;

    for (let month = 0; month < totalMonths; month++) {
        if (month > 0 && month % 12 === 0) {
            contribution *= (1 + annualIncrease / 100);
        }
        balance = balance * (1 + monthlyReturn) + contribution;
    }

    return Math.round(balance);
}

// Adjust a value for inflation to show in today's dollars
function adjustForInflation(amount, years) {
    return amount / Math.pow(1 + INFLATION_RATE / 100, years);
}

// Adjust projection data for inflation (each year's values discounted to today's dollars)
function adjustProjectionForInflation(data) {
    return data.map(d => ({
        ...d,
        p10: Math.round(adjustForInflation(d.p10, d.year)),
        p50: Math.round(adjustForInflation(d.p50, d.year)),
        p90: Math.round(adjustForInflation(d.p90, d.year)),
        contributions: Math.round(adjustForInflation(d.contributions, d.year)),
        range: [
            Math.round(adjustForInflation(d.range[0], d.year)),
            Math.round(adjustForInflation(d.range[1], d.year))
        ],
    }));
}

// ============================================================================
// FORMATTING (uses shared formatters from utils/formatters.js)
// ============================================================================

// ============================================================================
// COMPONENTS
// ============================================================================

function InsightCard({ icon, label, value, subtext, color = "text-gray-900", tooltip }) {
    return (
        <div className="text-center relative group">
            <p className="text-sm text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-center gap-1">
                {label}
                {tooltip && (
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
            </p>
            <p className={`text-3xl font-bold ${color}`}>{value}</p>
            {subtext && <p className="text-sm text-gray-500 mt-2">{subtext}</p>}
            {tooltip && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-white border border-gray-200 shadow-md rounded-lg text-xs text-gray-700 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 text-left">
                    {tooltip}
                </div>
            )}
        </div>
    );
}

function ProgressBar({ current, goal }) {
    const progress = Math.min((current / goal) * 100, 100);
    return (
        <div className="w-full">
            <div className="flex justify-between text-sm text-gray-500 mb-3">
                <span>Progress to goal</span>
                <span className="font-medium">{progress.toFixed(0)}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-accent-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>
            <div className="flex justify-between text-sm mt-3">
                <span className="text-gray-500">{formatCurrencyShort(current)}</span>
                <span className="text-gray-500">{formatCurrencyShort(goal)}</span>
            </div>
        </div>
    );
}

function ScenarioCard({ id, label, description, enabled, amount, onToggle, onAmountChange, prefix = "$", suffix = "" }) {
    return (
        <div className={`p-4 rounded-xl border transition-all ${enabled ? 'bg-white border-accent-500' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0 mr-3">
                    <p className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>{label}</p>
                    {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
                </div>
                <label className="relative cursor-pointer flex-shrink-0">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => onToggle(id)}
                        className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-500 transition-colors" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </label>
            </div>
            {enabled && onAmountChange && (
                <div className="mt-3">
                    <div className="relative">
                        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
                        <input
                            type="number"
                            value={amount || ""}
                            onChange={(e) => onAmountChange(id, parseInt(e.target.value) || 0)}
                            className="input w-full text-sm pl-7 pr-16"
                            step={50}
                        />
                        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{suffix}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}

function ProjectionChart({ data, goal, milestones, showRange, selectedMilestone }) {
    const maxVal = Math.max(...data.map(d => d.p90), goal || 0) * 1.1;

    // Find the year when selected milestone is reached
    const milestonePoint = selectedMilestone ? (() => {
        for (let i = 0; i < data.length; i++) {
            if (data[i].p50 >= selectedMilestone) {
                return { year: data[i].year, value: data[i].p50, milestone: selectedMilestone };
            }
        }
        return null;
    })() : null;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const d = payload[0]?.payload;
            if (!d) return null;
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md">
                    <p className="text-gray-900 font-medium mb-2">Year {label}</p>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Expected:</span>
                            <span className="text-accent-600 font-medium">{formatCurrencyCompact(d.p50)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Contributed:</span>
                            <span className="text-gray-700">{formatCurrencyCompact(d.contributions)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Growth:</span>
                            <span className="text-accent-600">{formatCurrencyCompact(d.p50 - d.contributions)}</span>
                        </div>
                        {showRange && (
                            <div className="border-t border-gray-200 pt-2 mt-2 text-xs text-gray-500">
                                Range: {formatCurrencyShort(d.p10)} - {formatCurrencyShort(d.p90)}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                        <linearGradient id="rangeGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2563EB" stopOpacity={0.15} />
                            <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                        </linearGradient>
                    </defs>

                    <XAxis
                        dataKey="year"
                        stroke="#94A3B8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(y) => y === 0 ? 'Now' : `${y}y`}
                    />
                    <YAxis
                        stroke="#94A3B8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => formatCurrencyShort(v)}
                        domain={[0, maxVal]}
                        width={60}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Goal line */}
                    {goal > 0 && (
                        <ReferenceLine
                            y={goal}
                            stroke="#f59e0b"
                            strokeDasharray="6 4"
                            strokeWidth={2}
                        />
                    )}

                    {/* Milestone lines */}
                    {milestones.map((m) => (
                        <ReferenceLine
                            key={m}
                            y={m}
                            stroke="#E2E8F0"
                            strokeDasharray="3 3"
                            strokeWidth={1}
                        />
                    ))}

                    {/* Confidence band */}
                    {showRange && (
                        <>
                            <Area
                                type="monotone"
                                dataKey="range"
                                fill="url(#rangeGradient)"
                                stroke="none"
                            />
                            {/* P90 upper bound line */}
                            <Line
                                type="monotone"
                                dataKey="p90"
                                stroke="#2563EB"
                                strokeWidth={1.5}
                                strokeOpacity={0.4}
                                dot={false}
                            />
                            {/* P10 lower bound line */}
                            <Line
                                type="monotone"
                                dataKey="p10"
                                stroke="#2563EB"
                                strokeWidth={1.5}
                                strokeOpacity={0.4}
                                dot={false}
                            />
                        </>
                    )}

                    {/* Main projection line */}
                    <Line
                        type="monotone"
                        dataKey="p50"
                        stroke="#2563EB"
                        strokeWidth={3}
                        dot={false}
                    />

                    {/* Starting point marker */}
                    <ReferenceDot
                        x={0}
                        y={data[0]?.p50}
                        r={6}
                        fill="#2563EB"
                        stroke="#FFFFFF"
                        strokeWidth={2}
                    />

                    {/* Selected milestone marker */}
                    {milestonePoint && (
                        <ReferenceDot
                            x={milestonePoint.year}
                            y={milestonePoint.value}
                            r={8}
                            fill="#f59e0b"
                            stroke="#FFFFFF"
                            strokeWidth={2}
                            label={{
                                value: `${formatCurrencyShort(milestonePoint.milestone)}`,
                                position: 'top',
                                fill: '#f59e0b',
                                fontSize: 12,
                                fontWeight: 600,
                                offset: 15,
                            }}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

function LockedSettingsOverlay() {
    return (
        <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
            <div className="relative w-full max-w-sm bg-white/95 backdrop-blur-sm border-l border-gray-200 p-6 overflow-y-auto pointer-events-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
                </div>

                {/* Locked overlay content */}
                <div className="flex flex-col items-center justify-center h-[calc(100%-4rem)]">
                    <div className="text-center max-w-xs">
                        <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-accent-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h3 className="text-gray-900 font-semibold text-lg mb-2">Unlock Full Planning</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Set custom goals, adjust contributions, change your risk profile, and run what-if scenarios.
                        </p>
                        <Link to="/signup" className="btn-primary px-8 py-3 text-sm inline-block">
                            Start Free Trial
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DemoProjectionsTab() {
    const [showSettings, setShowSettings] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState(null);

    // Use fixed settings from constant
    const settings = DEMO_SETTINGS;
    const currentWealth = DEMO_PROJECTIONS_INITIAL.investmentBalance;
    const expectedReturn = useMemo(() => calculateExpectedReturn(settings.allocation), [settings.allocation]);

    // Scenario Builder state
    const [scenarios, setScenarios] = useState({
        increaseSavings: { enabled: false, amount: Math.round(settings.monthlyContribution * 0.25) },
        cutExpenses: { enabled: false, amount: Math.round(DEMO_SAVINGS_RATE.avgMonthlyExpenses * 0.2) },
        max401k: { enabled: false, amount: 1958 },
        lumpSum: { enabled: false, amount: 10000 },
        rothIRA: { enabled: false, amount: 583 },
        aggressiveAllocation: { enabled: false, amount: 0 },
    });

    const handleScenarioToggle = (id) => {
        setScenarios(prev => ({
            ...prev,
            [id]: { ...prev[id], enabled: !prev[id].enabled },
        }));
    };

    const handleScenarioAmountChange = (id, amount) => {
        setScenarios(prev => ({
            ...prev,
            [id]: { ...prev[id], amount },
        }));
    };

    const anyScenarioEnabled = Object.values(scenarios).some(s => s.enabled);

    // Build effective parameters (base + any active scenarios)
    const effectiveParams = useMemo(() => {
        let contribution = settings.monthlyContribution;
        let starting = currentWealth;
        let returnRate = expectedReturn;
        let volatility = settings.volatility;

        if (scenarios.increaseSavings.enabled) contribution += scenarios.increaseSavings.amount;
        if (scenarios.cutExpenses.enabled) contribution += scenarios.cutExpenses.amount;
        if (scenarios.max401k.enabled) contribution += scenarios.max401k.amount;
        if (scenarios.lumpSum.enabled) starting += scenarios.lumpSum.amount;
        if (scenarios.rothIRA.enabled) contribution += scenarios.rothIRA.amount;
        if (scenarios.aggressiveAllocation.enabled) {
            returnRate = calculateExpectedReturn(SCENARIOS.aggressive.allocation);
            volatility = SCENARIOS.aggressive.volatility;
        }

        return { contribution, starting, returnRate, volatility };
    }, [scenarios, currentWealth, settings.monthlyContribution, settings.volatility, expectedReturn]);

    // Monte Carlo projection uses effective (scenario-adjusted) params
    const projection = useMemo(() => {
        return runProjection({
            startingAmount: effectiveParams.starting,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: settings.annualIncrease,
            expectedReturn: effectiveParams.returnRate,
            volatility: effectiveParams.volatility,
            years: settings.years,
        });
    }, [effectiveParams, settings.annualIncrease, settings.years]);

    const probability = useMemo(() => {
        return calculateProbability({
            startingAmount: effectiveParams.starting,
            targetAmount: settings.goal,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: settings.annualIncrease,
            years: settings.years,
            expectedReturn: effectiveParams.returnRate,
            volatility: effectiveParams.volatility,
        });
    }, [effectiveParams, settings.goal, settings.annualIncrease, settings.years]);

    // Apply inflation adjustment if enabled
    const displayProjection = useMemo(() => {
        return settings.adjustForInflation ? adjustProjectionForInflation(projection) : projection;
    }, [projection, settings.adjustForInflation]);

    // Goal is in nominal dollars, so always use nominal projection to find when goal is reached
    const goalReach = useMemo(() => findYearToReachGoal(projection, settings.goal), [projection, settings.goal]);
    const finalAmount = displayProjection[displayProjection.length - 1]?.p50 || 0;
    const milestones = useMemo(() => getRelevantMilestones(currentWealth, finalAmount), [currentWealth, finalAmount]);
    const monthlyRetirementIncome = Math.round((finalAmount * 0.04) / 12);

    // Baseline deterministic for scenario impact comparison
    const baseFinalDeterministic = useMemo(() => {
        return runDeterministicProjection({
            startingAmount: currentWealth,
            monthlyContribution: settings.monthlyContribution,
            annualIncrease: settings.annualIncrease,
            expectedReturn,
            years: settings.years,
        });
    }, [currentWealth, settings, expectedReturn]);

    // Scenario impact summary (comparing scenario projection to baseline)
    const scenarioImpact = useMemo(() => {
        if (!anyScenarioEnabled) return null;

        const baselineFinal = baseFinalDeterministic;
        const scenarioFinal = runDeterministicProjection({
            startingAmount: effectiveParams.starting,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: settings.annualIncrease,
            expectedReturn: effectiveParams.returnRate,
            years: settings.years,
        });

        let difference = scenarioFinal - baselineFinal;
        if (settings.adjustForInflation) {
            difference = Math.round(adjustForInflation(difference, settings.years));
        }

        let baseFinalDisplay = baselineFinal;
        if (settings.adjustForInflation) {
            baseFinalDisplay = Math.round(adjustForInflation(baselineFinal, settings.years));
        }
        const scenarioFinalDisplay = baseFinalDisplay + difference;

        // Calculate years sooner/later to reach goal
        let yearsDiff = 0;
        let baseYears = null;
        for (let y = 1; y <= settings.years; y++) {
            const v = runDeterministicProjection({
                startingAmount: currentWealth,
                monthlyContribution: settings.monthlyContribution,
                annualIncrease: settings.annualIncrease,
                expectedReturn,
                years: y,
            });
            if (v >= settings.goal) { baseYears = y; break; }
        }
        if (baseYears) {
            for (let y = 1; y <= settings.years; y++) {
                const v = runDeterministicProjection({
                    startingAmount: effectiveParams.starting,
                    monthlyContribution: effectiveParams.contribution,
                    annualIncrease: settings.annualIncrease,
                    expectedReturn: effectiveParams.returnRate,
                    years: y,
                });
                if (v >= settings.goal) { yearsDiff = baseYears - y; break; }
            }
        }

        const baseRetirement = Math.round((baseFinalDisplay * 0.04) / 12);
        const scenarioRetirement = Math.round((scenarioFinalDisplay * 0.04) / 12);

        return {
            baseTotal: baseFinalDisplay,
            scenarioTotal: scenarioFinalDisplay,
            difference,
            yearsDiff,
            baseRetirement,
            scenarioRetirement,
            retirementDiff: scenarioRetirement - baseRetirement,
        };
    }, [anyScenarioEnabled, effectiveParams, settings, baseFinalDeterministic, currentWealth, expectedReturn]);

    // Total contributions vs growth
    const totalContributions = displayProjection[displayProjection.length - 1]?.contributions || 0;
    const growthAmount = finalAmount - totalContributions;

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-10">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Your Wealth Projection</h1>
                    <p className="text-gray-500 mt-2 text-base sm:text-lg">
                        Based on {formatCurrencyCompact(currentWealth)} invested today
                        <span className="ml-2 px-2 py-0.5 bg-accent-50 text-accent-600 text-sm rounded">
                            Demo
                        </span>
                    </p>
                </div>
                <button
                    onClick={() => setShowSettings(true)}
                    className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-base w-full sm:w-auto"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Customize
                </button>
            </div>

            {/* Hero Insight */}
            <div className="card p-6 sm:p-10 mb-6 sm:mb-8 text-center">
                {goalReach ? (
                    <>
                        <p className="text-gray-500 mb-3 text-base sm:text-lg">At your current pace, you'll reach</p>
                        <p className="text-3xl sm:text-5xl font-bold text-gray-900 mb-3">{formatCurrencyCompact(settings.goal)}</p>
                        <p className="text-xl sm:text-2xl text-accent-600 font-semibold">
                            by {new Date().getFullYear() + goalReach.year}
                        </p>
                        <p className="text-gray-500 mt-4 text-sm sm:text-base">
                            That's {goalReach.year} years from now
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-gray-500 mb-3 text-base sm:text-lg">In {settings.years} years, you could have</p>
                        <p className="text-3xl sm:text-5xl font-bold text-gray-900 mb-3">{formatCurrencyCompact(finalAmount)}</p>
                        <p className="text-sm sm:text-base text-gray-500 mt-4">
                            {probability.toFixed(0)}% chance of reaching {formatCurrencyCompact(settings.goal)}
                        </p>
                    </>
                )}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="card p-6">
                    <InsightCard
                        label="Probability"
                        value={`${probability.toFixed(0)}%`}
                        subtext="of reaching goal"
                        color={probability >= 70 ? "text-accent-600" : probability >= 50 ? "text-amber-600" : "text-danger-500"}
                        tooltip={`Based on 1,000 Monte Carlo simulations using your ${settings.allocation.stocks}% stocks / ${settings.allocation.bonds}% bonds allocation, ${settings.monthlyContribution.toLocaleString()}/month contribution, and ${settings.annualIncrease}% annual increase.`}
                    />
                </div>
                <div className="card p-6">
                    <InsightCard
                        label="Projected"
                        value={formatCurrencyShort(finalAmount)}
                        subtext={`in ${settings.years} years`}
                        color="text-accent-600"
                        tooltip={`The median (P50) outcome from simulations. This means half of scenarios end higher and half end lower. Your ${formatCurrencyShort(totalContributions)} in contributions + ${formatCurrencyShort(growthAmount)} in expected investment growth.`}
                    />
                </div>
                <div className="card p-6">
                    <InsightCard
                        label="Retirement Income"
                        value={formatCurrencyCompact(monthlyRetirementIncome)}
                        subtext="per month (4% rule)"
                        color="text-gray-900"
                        tooltip={`Based on the 4% safe withdrawal rate. This means withdrawing 4% of your portfolio annually (${formatCurrencyCompact(finalAmount * 0.04)}/year) which historically has sustained retirees for 30+ years.`}
                    />
                </div>
            </div>

            {/* Progress Bar */}
            <div className="card p-4 sm:p-6 mb-6 sm:mb-8">
                <ProgressBar current={currentWealth} goal={settings.goal} />
            </div>

            {/* Chart */}
            <div className="card p-4 sm:p-8 mb-6 sm:mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Growth Trajectory</h3>
                    <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-0.5 bg-accent-500 rounded"></span> Projected
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-0.5 bg-amber-500 rounded" style={{borderStyle: 'dashed'}}></span> Goal
                        </span>
                    </div>
                </div>
                <ProjectionChart
                    data={displayProjection}
                    goal={settings.goal}
                    milestones={milestones}
                    showRange={settings.showRange}
                    selectedMilestone={selectedMilestone}
                />
                {/* Milestone labels - only show reachable milestones */}
                {(() => {
                    const reachableMilestones = milestones.filter(m => findYearToReachGoal(displayProjection, m));
                    if (reachableMilestones.length === 0) return null;
                    return (
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-6 pt-6 border-t border-gray-200">
                            <span className="text-sm text-gray-500">Milestones:</span>
                            {reachableMilestones.map((m) => {
                                const yearToReach = findYearToReachGoal(displayProjection, m);
                                const isSelected = selectedMilestone === m;
                                return (
                                    <button
                                        key={m}
                                        onClick={() => setSelectedMilestone(isSelected ? null : m)}
                                        className={`text-sm px-3 py-1.5 rounded-lg transition-all ${
                                            isSelected
                                                ? 'bg-amber-500 text-gray-900 font-medium'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                    >
                                        {formatCurrencyShort(m)} ({yearToReach.year}y)
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>

            {/* Scenario Builder */}
            <div className="card p-4 sm:p-8 mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 text-center">Scenario Builder</h3>
                <p className="text-sm text-gray-500 text-center mb-6">Toggle scenarios to see how they change your projection</p>

                {/* Cumulative Impact Summary */}
                {scenarioImpact && (
                    <div className="mb-6 p-5 rounded-xl bg-accent-50 border border-accent-200">
                        <p className="text-sm text-gray-500 mb-2 text-center">With these changes</p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-3">
                            <span className="text-lg text-gray-500">{formatCurrencyShort(scenarioImpact.baseTotal)}</span>
                            <svg className="w-5 h-5 text-accent-600 rotate-90 sm:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                            <span className="text-2xl font-bold text-gray-900">{formatCurrencyShort(scenarioImpact.scenarioTotal)}</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
                            <span className={`font-medium ${scenarioImpact.difference >= 0 ? 'text-accent-600' : 'text-danger-500'}`}>
                                {scenarioImpact.difference >= 0 ? '+' : ''}{formatCurrencyShort(scenarioImpact.difference)}
                            </span>
                            {scenarioImpact.yearsDiff > 0 && (
                                <span className="text-accent-600 font-medium">
                                    {scenarioImpact.yearsDiff} {scenarioImpact.yearsDiff === 1 ? 'year' : 'years'} sooner
                                </span>
                            )}
                            {scenarioImpact.yearsDiff < 0 && (
                                <span className="text-danger-500 font-medium">
                                    {Math.abs(scenarioImpact.yearsDiff)} {Math.abs(scenarioImpact.yearsDiff) === 1 ? 'year' : 'years'} later
                                </span>
                            )}
                            <span className={`font-medium ${scenarioImpact.retirementDiff >= 0 ? 'text-accent-600' : 'text-danger-500'}`}>
                                {scenarioImpact.retirementDiff >= 0 ? '+' : ''}{formatCurrencyCompact(scenarioImpact.retirementDiff)}/mo retirement income
                            </span>
                        </div>
                    </div>
                )}

                {/* Scenario Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ScenarioCard
                        id="increaseSavings"
                        label="Increase monthly savings"
                        description={`Default: 25% of your ${formatCurrencyCompact(settings.monthlyContribution)}/mo contribution`}
                        enabled={scenarios.increaseSavings.enabled}
                        amount={scenarios.increaseSavings.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    <ScenarioCard
                        id="cutExpenses"
                        label="Cut expenses"
                        description={`Default: 20% of your ${formatCurrencyCompact(DEMO_SAVINGS_RATE.avgMonthlyExpenses)}/mo spending`}
                        enabled={scenarios.cutExpenses.enabled}
                        amount={scenarios.cutExpenses.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    <ScenarioCard
                        id="max401k"
                        label="Max out 401(k)"
                        description="$23,500/year ($1,958/month) added to contributions"
                        enabled={scenarios.max401k.enabled}
                        amount={scenarios.max401k.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    <ScenarioCard
                        id="lumpSum"
                        label="Invest a lump sum today"
                        description="One-time amount added to your starting balance"
                        enabled={scenarios.lumpSum.enabled}
                        amount={scenarios.lumpSum.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                    />

                    <ScenarioCard
                        id="rothIRA"
                        label="Start Roth IRA"
                        description="$7,000/year ($583/month) added to contributions"
                        enabled={scenarios.rothIRA.enabled}
                        amount={scenarios.rothIRA.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    <ScenarioCard
                        id="aggressiveAllocation"
                        label="Switch to aggressive allocation"
                        description="90% stocks / 5% bonds / 5% cash"
                        enabled={scenarios.aggressiveAllocation.enabled}
                        onToggle={handleScenarioToggle}
                    />
                </div>
            </div>

            {/* Disclaimer */}
            <p className="text-sm text-gray-400 text-center pb-8">
                Projections are based on historical averages and Monte Carlo simulation.
                Actual results will vary. Past performance doesn't guarantee future results.
            </p>

            {/* Demo CTA */}
            <div className="mb-8">
                <DemoCTA variant="inline" />
            </div>

            {/* Settings Panel - Locked for demo */}
            {showSettings && (
                <>
                    <div className="fixed inset-0 z-40 bg-gray-900/40" onClick={() => setShowSettings(false)} />
                    <LockedSettingsOverlay />
                    <button
                        onClick={() => setShowSettings(false)}
                        className="fixed top-6 right-6 z-50 text-gray-500 hover:text-gray-900"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </>
            )}
        </div>
    );
}
